import { CustomJoyrideStep } from 'components/facets/CustomJoyrideTour'
import { editUserAttribute } from 'lib/api'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ACTIONS, CallBackProps, STATUS } from 'react-joyride'
import { AppUser, PurchaseEvent } from 'shared'
import { SetState } from 'types'
import { waitForSelector } from 'utils/lib'
import { useTaxationStore } from './useTaxationStore'

type Args = {
  user: AppUser
  setUser: SetState<AppUser>
  run: boolean
  setRun: (v: boolean) => void
  stepIndex: number
  setStepIndex: (v: number | ((p: number) => number)) => void
  loading: boolean
  peStore: PurchaseEvent[]
  hasPurchases: boolean
  view: string
}

const IDX = {
  EXPORT_BOX: 'exportBox',
  MARGIN_BOX: 'marginBox',
  CREATE_PE: 'createPe',
  EMPTY_PE: 'emptyState',
  FILTER_PE: 'filterPe',
  EXPORT_ARTICLES: 'exportArticles',
  UPLOAD_DOCUMENT: 'uploadDocument',
  LINKED_PRICE: 'linkedPrice',
  LINKED_QTY: 'linkedQty',
  INSPECT_PE: 'inspectPe',
  DRAWER_RECEIPT: 'drawerReceipt',
  EDIT_PE: 'editPe',
  PE_STRATEGY: 'peStrategy',
  EMPTY_PURCHASES: 'emptyPurchasesTable',
  ARTICLE_QUANTITY: 'articleQuantity',
  ARTICLE_UNIT_PRICE: 'articleUnitPrice',
  ARTICLE_STRATEGY: 'articleStrategy',
  ARTICLE_RESET: 'articleReset',
  ARTICLE_DELETE: 'articleDelete',
  END_TOUR: 'endTour',
} as const

export function usePurchaseEventsTour({
  user,
  setUser,
  run,
  setRun,
  stepIndex,
  setStepIndex,
  loading,
  peStore,
  hasPurchases,
  view,
}: Args) {
  const { t } = useTranslation()
  const [startingIndexTour, setStartingIndexTour] = useTaxationStore((s) => [
    s.startingIndexTour,
    s.setStartingIndexTour,
  ])
  const hasPurchaseEvents = peStore.length > 0
  const isResumingRef = useRef(false)
  const stepIndexRef = useRef(stepIndex)
  stepIndexRef.current = stepIndex
  const timeoutIdsRef = useRef<number[]>([])
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      timeoutIdsRef.current.forEach((id) => clearTimeout(id))
      timeoutIdsRef.current = []
    }
  }, [])

  /**
   * Memoize `steps` to keep its reference stable across renders.
   *
   * This prevents unnecessary side effects, such as:
   * - `idxById` changing identity on every render
   * - `useEffect` hooks being triggered even when the steps have not actually changed
   *
   * `t` is included as a dependency so that when the user changes language,
   * the step contents are regenerated with the updated translations.
   */
  const steps: CustomJoyrideStep[] = useMemo<CustomJoyrideStep[]>(() => {
    const base: CustomJoyrideStep[] = [
      {
        id: IDX.EXPORT_BOX,
        target: '[data-testid="tax-export-box"]',
        content: t('taxationTour.exportBox'),
      },
      {
        id: IDX.MARGIN_BOX,
        target: '[data-testid="tax-margin-box"]',
        content: t('taxationTour.marginBox'),
      },
      {
        id: IDX.CREATE_PE,
        target: '[data-testid="manage-pe"]',
        content: t(`purchaseEventsTour.${IDX.CREATE_PE}`),
      },
      {
        id: IDX.EMPTY_PE,
        target: '[data-testid="manage-pe"]',
        content: t(`purchaseEventsTour.${IDX.EMPTY_PE}`),
        placement: 'right',
      },
      {
        id: IDX.FILTER_PE,
        target: '[data-testid="filter-pe"]',
        content: t(`purchaseEventsTour.${IDX.FILTER_PE}`),
        placement: 'left',
      },
      {
        id: IDX.EXPORT_ARTICLES,
        target: '[data-testid="csv-0"]',
        content: t(`purchaseEventsTour.${IDX.EXPORT_ARTICLES}`),
      },
      {
        id: IDX.UPLOAD_DOCUMENT,
        target: '[data-testid="receipt-0"]',
        content: t(`purchaseEventsTour.${IDX.UPLOAD_DOCUMENT}`),
      },
      {
        id: IDX.LINKED_PRICE,
        target: '[data-testid="linked-price-0"]',
        content: t(`purchaseEventsTour.${IDX.LINKED_PRICE}`),
      },
      {
        id: IDX.LINKED_QTY,
        target: '[data-testid="linked-qty-0"]',
        content: t(`purchaseEventsTour.${IDX.LINKED_QTY}`),
      },
      {
        id: IDX.INSPECT_PE,
        target: '[data-testid="inspect-pe-0"]',
        content: t(`purchaseEventsTour.${IDX.INSPECT_PE}`),
        placement: 'right',
      },
      {
        id: IDX.DRAWER_RECEIPT,
        target: '[data-testid="drawer-receipt-section"]',
        content: t(`purchaseEventsTour.${IDX.DRAWER_RECEIPT}`),
      },
      {
        id: IDX.EDIT_PE,
        target: '[data-testid="edit-pe"]',
        content: t(`purchaseEventsTour.${IDX.EDIT_PE}`),
        meta: {
          clickSelector: '[data-testid="edit-pe"]',
          // NOTE: we intentionally don't wait for re-split-btn here. That button lives inside
          // the price-mismatch alert, so it only exists when the purchase event has a price
          // mismatch. Waiting for it would hang the tour on consistent purchases. The EDIT_PE
          // step:after handler below decides whether to show PE_STRATEGY or skip past it.
        },
      },
      {
        id: IDX.PE_STRATEGY,
        target: '[data-testid="re-split-btn"]',
        content: t(`purchaseEventsTour.${IDX.PE_STRATEGY}`),
      },
      {
        id: IDX.EMPTY_PURCHASES,
        target: '[data-testid="purchases-table"]',
        placement: 'left',
        content: t(`purchaseEventsTour.${IDX.EMPTY_PURCHASES}`),
      },
      {
        id: IDX.ARTICLE_QUANTITY,
        target: '[data-testid="edit-quantity-0"]',
        content: t(`purchaseEventsTour.${IDX.ARTICLE_QUANTITY}`),
      },
      {
        id: IDX.ARTICLE_UNIT_PRICE,
        target: '[data-testid="edit-price-0"]',
        content: t(`purchaseEventsTour.${IDX.ARTICLE_UNIT_PRICE}`),
      },
      {
        id: IDX.ARTICLE_STRATEGY,
        target: '[data-testid="strategy-0"]',
        content: t(`purchaseEventsTour.${IDX.ARTICLE_STRATEGY}`),
      },
      {
        id: IDX.ARTICLE_RESET,
        target: '[data-testid="reset-0"]',
        content: t(`purchaseEventsTour.${IDX.ARTICLE_RESET}`),
        placement: 'left',
      },
      {
        id: IDX.ARTICLE_DELETE,
        target: '[data-testid="delete-0"]',
        content: t(`purchaseEventsTour.${IDX.ARTICLE_DELETE}`),
        placement: 'left',
      },
      {
        id: IDX.END_TOUR,
        target: 'body',
        placement: 'center',
        content: t(`listingTour.${IDX.END_TOUR}`),
      },
    ]
    return base
  }, [t])

  const idxById = useCallback((id: string) => steps.findIndex((s) => s.id === id), [steps])

  /**
   * Redirect logic for the tour when the "empty" state is no longer valid.   *
   * Scenario:
   * - The tour is running (run === true)
   * - We are currently showing the EMPTY_PE step (which explains that there are no Purchase Events)
   * - Meanwhile, Purchase Events are loaded/created (so hasPurchaseEvents becomes true)
   *
   * At this point, the current step becomes inconsistent with the actual UI state,
   * so we need to perform a controlled jump to the FILTER_PE step.
   *
   * Flow:
   * 1) Ensure the tour is running and the UI is not in a loading state.
   * 2) Ensure the current step is EMPTY_PE.
   * 3) If Purchase Events now exist, wait for the next-step UI to be ready (the filter element exists in the DOM).
   * 4) Perform a "hard reset" of Joyride (setRun(false)) to avoid internal glitches.
   * 5) On the next tick (setTimeout 0), set the stepIndex to FILTER_PE and restart the tour (setRun(true)).
   *
   * Reset + restart is a common Joyride technique to handle dynamic step jumps in a stable way,
   * preventing tooltip targeting/positioning issues.
   *
   * Mental model: run === true -> current step = EMPTY_PE -> hasPurchaseEvents becomes true -> wait until UI is ready (filter-pe exists)
   * -> setRun(false)  → Joyride unmounts -> next tick: setStepIndex(FILTER_PE), setRun(true) -> Tour restarts from the step that matches the updated UI state
   */

  useEffect(() => {
    if (!run) return
    if (steps[stepIndex]?.id !== IDX.EMPTY_PE) return
    if (!hasPurchaseEvents) return
    ;(async () => {
      const ok = await waitForSelector('[data-testid="filter-pe"]', 4000)
      if (!ok) return

      // hard reset Joyride
      setRun(false)
      // microtask/tick dopo: riavvia sullo step giusto
      const tid1 = window.setTimeout(() => {
        if (!isMountedRef.current) return
        setStepIndex(idxById(IDX.FILTER_PE))
        setRun(true)
      }, 0)
      timeoutIdsRef.current.push(tid1)
    })()
  }, [run, loading, hasPurchaseEvents, stepIndex, idxById, steps])

  // 2) Resume automatico: se sei su EMPTY_PURCHASES e arrivano purchases
  useEffect(() => {
    if (loading) return
    if (view !== 'purchases') return
    if (startingIndexTour === 0) return
    if (isResumingRef.current) return
    if (steps[stepIndexRef.current]?.id === IDX.EMPTY_PURCHASES) return

    // // se il drawer è già aperto, NON fare resume (eviti back-jump)
    // if (document.querySelector('[data-testid="edit-pe"]')) return

    // This is a manual mutex/lock: “I’m doing resume from here on, don’t let me restart the effect.”
    isResumingRef.current = true
    ;(async () => {
      // Wait for the clickable line to open the drawer:
      const okRow = await waitForSelector('[data-testid="inspect-pe-0"]', 4000)
      if (!okRow) return // Click the line to open the drawer:
      ;(document.querySelector('[data-testid="inspect-pe-0"]') as HTMLElement | null)?.click()

      // Wait for the drawer to mount (receipt section is always present):
      const okDrawer = await waitForSelector('[data-testid="drawer-receipt-section"]', 4000)
      if (!okDrawer) return

      await new Promise((r) => {
        const tid2 = window.setTimeout(r, 350)
        timeoutIdsRef.current.push(tid2)
      })
      if (!isMountedRef.current)
        return // Click the edit button only if it exists. When the drawer opens already in
        // editing mode (e.g. because of a mismatch) the button isn't rendered and we
        // don't need to click it.
      ;(document.querySelector('[data-testid="edit-pe"]') as HTMLElement | null)?.click()

      // At this point the UI is ready → resume the tour from the saved step and “consume” the resume:
      // HARD RESET so Joyride re-syncs with the new index + new DOM layout
      setRun(false)
      const tid3 = window.setTimeout(() => {
        if (!isMountedRef.current) return
        setStepIndex(startingIndexTour)
        setRun(true)
        setStartingIndexTour(0) // consume resume (NOW it's safe)
      }, 0)
      timeoutIdsRef.current.push(tid3)
    })().finally(() => {
      isResumingRef.current = false
    })
  }, [loading, view, startingIndexTour, steps])

  const runTour = useCallback(
    async (data: CallBackProps) => {
      const { status, type, index, action } = data
      const stepId = steps[index]?.id

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false)
        const notifications = user.enabledNotifications ?? {}
        const response = await editUserAttribute(
          {
            ...notifications,
            displayPurchaseEventsTour: false,
          },
          'enabledNotifications'
        )
        setUser({ ...user, ...response })
        return
      }

      if (type === 'error:target_not_found') {
        // keep the tour on the same step, but only after the target exists again
        const targetSelector = steps[index]?.target as string

        if (typeof targetSelector === 'string' && targetSelector !== 'body') {
          const ok = await waitForSelector(targetSelector, 4000)
          if (ok) setStepIndex(index)
        } else {
          setStepIndex(index)
        }
        return
      }

      if (type === 'step:before') {
        if (stepId === IDX.END_TOUR) {
          void (document.querySelector('[data-testid="close-btn"]') as HTMLElement | null)?.click()
          await new Promise((r) => {
            const tid = window.setTimeout(r, 250)
            timeoutIdsRef.current.push(tid)
          })
          if (!isMountedRef.current) return
        }
      }

      if (type !== 'step:after') return

      // meta click+wait (solo per step:after)
      {
        const step = steps[index]
        const { clickSelector, waitForSelector: waitSel } = step?.meta ?? {}

        if (clickSelector) {
          void (document.querySelector(clickSelector) as HTMLElement | null)?.click()
        }

        if (waitSel) {
          const ok = await waitForSelector(waitSel)
          if (!ok) return
        }
      }

      /**
       * "I've shown CREATE_PE. Now the user presses Next. Where should I go?"
       * There are two branches:
       * - The user has NOT created anything -> Go to the EMPTY_PE step (explain that it's empty).
       * - The user already has PEs -> Go to the FILTER_PE step (but only after the filter is in the DOM).
       */
      if (stepId === IDX.CREATE_PE) {
        if (!hasPurchaseEvents) {
          setStepIndex(idxById(IDX.EMPTY_PE))
          return
        }

        // if there are already PEs, make sure the filter exists (UI ready)
        const ok = await waitForSelector('[data-testid="filter-pe"]', 3000)
        if (!ok) return
        setStepIndex(idxById(IDX.FILTER_PE))
        return
      }

      if (stepId === IDX.EMPTY_PE && !hasPurchaseEvents) {
        setStepIndex(index) // reiterates to joyride: we're staying here
        return
      }

      // in this case I don't use meta because I need to do something more than click and wait, that is, I set a timeout for the drawer to finish animating
      if (stepId === IDX.INSPECT_PE) {
        void (document.querySelector('[data-testid="inspect-pe-0"]') as HTMLElement | null)?.click()

        // wait for the drawer to mount (receipt section is always present when the drawer opens)
        const ok = await waitForSelector('[data-testid="drawer-receipt-section"]', 4000)
        if (!ok) return

        await new Promise((r) => {
          const tid = window.setTimeout(r, 350)
          timeoutIdsRef.current.push(tid)
        })
        if (!isMountedRef.current) return

        setStepIndex(idxById(IDX.DRAWER_RECEIPT))
        return
      }

      // After the receipt step: if the edit button is present, go to EDIT_PE.
      // If it's missing, the drawer has already opened in editing mode (e.g. because of a
      // quantity/price mismatch). In that case try to show PE_STRATEGY, but skip it when
      // re-split-btn isn't in the DOM (no price mismatch → button not rendered).
      if (stepId === IDX.DRAWER_RECEIPT) {
        const editBtn = document.querySelector('[data-testid="edit-pe"]')
        if (editBtn) {
          setStepIndex(idxById(IDX.EDIT_PE))
          return
        }

        if (document.querySelector('[data-testid="re-split-btn"]')) {
          setStepIndex(idxById(IDX.PE_STRATEGY))
          return
        }

        // No re-split-btn available: fall through to the post-PE_STRATEGY logic
        // (either EMPTY_PURCHASES or ARTICLE_QUANTITY depending on hasPurchases).
        if (!hasPurchases) {
          const ok = await waitForSelector('[data-testid="purchases-table"]', 4000)
          if (!ok) return
          setStepIndex(idxById(IDX.EMPTY_PURCHASES))
          return
        }
        setStepIndex(idxById(IDX.ARTICLE_QUANTITY))
        return
      }

      // After EDIT_PE the click on `edit-pe` has activated editing mode. The PE_STRATEGY
      // step targets re-split-btn which only exists when there's a price mismatch, so if
      // it's not around we skip PE_STRATEGY and jump to the next meaningful step.
      if (stepId === IDX.EDIT_PE) {
        // Small grace period for the DOM to update after the click.
        const resplitOk = await waitForSelector('[data-testid="re-split-btn"]', 1500)
        if (resplitOk) {
          setStepIndex(idxById(IDX.PE_STRATEGY))
          return
        }

        if (!hasPurchases) {
          const ok = await waitForSelector('[data-testid="purchases-table"]', 4000)
          if (!ok) return
          setStepIndex(idxById(IDX.EMPTY_PURCHASES))
          return
        }
        setStepIndex(idxById(IDX.ARTICLE_QUANTITY))
        return
      }

      if (stepId === IDX.PE_STRATEGY) {
        if (!hasPurchases) {
          const ok = await waitForSelector('[data-testid="purchases-table"]', 4000)
          if (!ok) return
          setStepIndex(idxById(IDX.EMPTY_PURCHASES))
          return
        }

        setStepIndex(idxById(IDX.ARTICLE_QUANTITY))
        return
      }

      if (stepId === IDX.EMPTY_PURCHASES) {
        setStartingIndexTour(idxById(IDX.ARTICLE_QUANTITY)) // when I reopen the page, I'll want to start from here
        return
      }

      const delta = action === ACTIONS.PREV ? -1 : 1
      setStepIndex(index + delta)
    },
    [
      user,
      steps,
      setRun,
      setStepIndex,
      idxById,
      hasPurchaseEvents,
      hasPurchases,
      setStartingIndexTour,
    ]
  )

  return { steps, runTour }
}
